import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500
};

/**
 * Bandholz marquee component.
 *
 * Based on Ritual's native marquee component, with optional hover slowdown
 * controlled by data-pause-on-hover.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper
 * @property {HTMLElement} content
 * @property {HTMLElement[]} marqueeItems
 *
 * @extends Component<Refs>
 */
class BandholzMarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;

    if (marqueeItems.length === 0) {
      return;
    }

    const { numberOfCopies } =
      await this.#queryNumberOfCopies();

    const speed =
      this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(speed);

    window.addEventListener(
      'resize',
      this.#handleResize
    );

    if (this.pauseOnHover) {
      this.addEventListener(
        'pointerenter',
        this.#slowDown
      );

      this.addEventListener(
        'pointerleave',
        this.#speedUp
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(
      'resize',
      this.#handleResize
    );

    this.removeEventListener(
      'pointerenter',
      this.#slowDown
    );

    this.removeEventListener(
      'pointerleave',
      this.#speedUp
    );
  }

  /**
   * Whether hover slowdown is enabled for this section.
   *
   * @returns {boolean}
   */
  get pauseOnHover() {
    return (
      this.dataset.pauseOnHover === 'true'
    );
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  #slowDown = debounce(() => {
    if (!this.pauseOnHover) {
      return;
    }

    if (this.#animation) {
      return;
    }

    const animation =
      this.refs.wrapper.getAnimations()[0];

    if (!animation) {
      return;
    }

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => {
        animation.updatePlaybackRate(value);
      },
      onComplete: () => {
        this.#animation = null;
      }
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp = () => {
    if (!this.pauseOnHover) {
      return;
    }

    this.#slowDown.cancel();

    const animation =
      this.refs.wrapper.getAnimations()[0];

    if (
      !animation ||
      animation.playbackRate === 1
    ) {
      return;
    }

    const from =
      this.#animation?.current ?? 0;

    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => {
        animation.updatePlaybackRate(value);
      },
      onComplete: () => {
        this.#animation = null;
      }
    });
  };

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild
      ? lastChild
      : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty(
      '--marquee-speed',
      `${value}s`
    );
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        return setTimeout(
          () => {
            resolve({
              numberOfCopies: 1,
              isHorizontalResize: true
            });
          },
          0
        );
      }

      const observer =
        new IntersectionObserver(
          (entries) => {
            const firstEntry = entries[0];

            if (!firstEntry) {
              return;
            }

            observer.disconnect();

            const {
              width: marqueeWidth
            } = firstEntry.rootBounds ?? {
              width: 0
            };

            const {
              width: marqueeItemsWidth
            } = firstEntry.boundingClientRect;

            const isHorizontalResize =
              this.#marqueeWidth !==
              marqueeWidth;

            this.#marqueeWidth =
              marqueeWidth;

            setTimeout(() => {
              resolve({
                numberOfCopies:
                  marqueeItemsWidth === 0
                    ? 1
                    : Math.ceil(
                        marqueeWidth /
                          marqueeItemsWidth
                      ),
                isHorizontalResize
              });
            }, 0);
          },
          {
            root: this
          }
        );

      observer.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor =
      Number(
        this.getAttribute(
          'data-speed-factor'
        )
      );

    return (
      Math.sqrt(numberOfCopies) *
      speedFactor
    );
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;

    const {
      numberOfCopies: newNumberOfCopies,
      isHorizontalResize
    } = await this.#queryNumberOfCopies();

    if (!isHorizontalResize) {
      return;
    }

    const currentNumberOfCopies =
      marqueeItems.length;

    const speed =
      this.#calculateSpeed(
        newNumberOfCopies
      );

    if (
      newNumberOfCopies >
      currentNumberOfCopies
    ) {
      this.#addRepeatedItems(
        newNumberOfCopies -
          currentNumberOfCopies
      );
    } else if (
      newNumberOfCopies <
      currentNumberOfCopies
    ) {
      this.#removeRepeatedItems(
        currentNumberOfCopies -
          newNumberOfCopies
      );
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations =
      this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone =
      /** @type {HTMLElement} */ (
        this.refs.content.cloneNode(true)
      );

    clone.setAttribute(
      'aria-hidden',
      'true'
    );

    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const {
      content,
      marqueeItems
    } = this.refs;

    if (!marqueeItems[0]) {
      return;
    }

    for (
      let index = 0;
      index < numberOfCopies - 1;
      index += 1
    ) {
      const clone =
        marqueeItems[0].cloneNode(true);

      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children =
      Array.from(content.children);

    const itemsToRemove =
      Math.min(
        numberOfCopies,
        children.length - 1
      );

    for (
      let index = 0;
      index < itemsToRemove;
      index += 1
    ) {
      content.lastElementChild?.remove();
    }
  }
}

/**
 * Animate a numeric value smoothly.
 *
 * @param {object} parameters
 * @param {number} parameters.from
 * @param {number} parameters.to
 * @param {number} parameters.duration
 * @param {(value: number) => void} parameters.onUpdate
 * @param {(progress: number) => number} [parameters.easing]
 * @param {() => void} [parameters.onComplete]
 * @returns {{ cancel: () => void, readonly current: number }}
 */
function animateValue({
  from,
  to,
  duration,
  onUpdate,
  easing = (progress) =>
    progress *
    progress *
    (3 - 2 * progress),
  onComplete
}) {
  const startTime =
    performance.now();

  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime
   */
  function animate(currentTime) {
    if (cancelled) {
      return;
    }

    const elapsed =
      currentTime - startTime;

    const progress =
      Math.min(
        elapsed / duration,
        1
      );

    const easedProgress =
      easing(progress);

    currentValue =
      from +
      (to - from) *
        easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (
      typeof onComplete === 'function'
    ) {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },

    cancel() {
      cancelled = true;
    }
  };
}

if (
  !customElements.get(
    'bandholz-marquee-component'
  )
) {
  customElements.define(
    'bandholz-marquee-component',
    BandholzMarqueeComponent
  );
}